import { Component, Prop, h, State, Event, EventEmitter } from '@stencil/core';
import { ButtonState } from './types';
import { VideoPortalManager } from '../../utils/video-portal-utils';

@Component({
  tag: 'vsr-link-button-v2',
  styleUrl: 'vsr-button.scss',
  shadow: false,
})
export class VsrLinkButton {
  @Prop() state: ButtonState = 'active';
  @Prop() href: string = '#';
  @Prop() target: string = '#';
  @Prop() themeOverride: string = '';
  @Prop() focused: boolean = false;
  @Prop() ctaLinkType: string = '';
  @Prop() ctaVideoUrl: string = '';
  @Prop() ctaVideoTitle: string = '';

  @State() isFocused: boolean = false;

  @Event() vsrClick: EventEmitter<MouseEvent>;

  // Video portal manager instance
  private videoPortalManager: VideoPortalManager = new VideoPortalManager();

  componentWillLoad() {
    this.isFocused = this.focused;
  }

  /**
   * Component lifecycle method that runs when the component is removed from the DOM
   */
  disconnectedCallback() {
    // Remove event listener
    window.removeEventListener('modalClosed', this.handleModalClosed);

    // Clean up the portal container
    this.videoPortalManager.closeModal();
  }

  private handleClick = (event: MouseEvent) => {
    this.vsrClick.emit(event);

    if (this.target === 'modal' && this.href) {
      event.preventDefault();

      const width = 1280;
      const height = Math.round((width * 9) / 16);

      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        this.href,
        'popupWindow',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
      );

      return;
    }

    if (this.ctaLinkType === 'video' && this.ctaVideoUrl) {
      event.preventDefault();

      window.addEventListener('modalClosed', this.handleModalClosed);

      this.videoPortalManager.openModal({
        videoTitle: this.ctaVideoTitle,
        videoUrl: this.ctaVideoUrl,
        theme: this.themeOverride === 'dark' ? 'dark' : 'light',
      });
    }
  };

  /**
   * Handle modal closed event
   */
  private handleModalClosed = () => {
    // Remove event listener
    window.removeEventListener('modalClosed', this.handleModalClosed);
  };

  /**
   * Get the appropriate link class based on target and link type
   */
  private getTargetClass(): string {
    // First check if it's a video link
    if (this.ctaLinkType === 'video' && this.ctaVideoUrl) {
      return 'vsr-button-video';
    }

    // Check if it's an external link
    if (this.ctaLinkType === 'external') {
      return 'vsr-button-external';
    }

    // Then check the target attribute
    if (!this.target) return '';

    switch (this.target) {
      case '_self':
        return 'vsr-button-same-window';
      case '_blank':
        return 'vsr-button-new-window';
      case 'modal':
        return 'vsr-button-modal';
      default:
        return '';
    }
  }

  render() {
    const linkClass = `link-button ${this.state.toLowerCase()} ${this.isFocused ? 'focused' : ''} ${this.getTargetClass()}`;

    return (
      <a
        data-theme={this.themeOverride}
        class={linkClass}
        href={this.href}
        target={this.target}
        onClick={this.handleClick}
        onFocus={() => (this.isFocused = true)}
        onBlur={() => (this.isFocused = false)}
        aria-disabled={this.state === 'inactive'}
      >
        <div class="container">
          <span class="button-content">
            <slot />
          </span>
        </div>
        {this.isFocused && <div class="focus-ring"></div>}
      </a>
    );
  }
}
